import { deepMerge } from 'grommet/utils'
import { Grommet } from 'grommet'
import { ReactNode, useEffect, useMemo } from 'react'
import { useAppSelector } from '../store/hooks'
import AppTheme from '../themes'
import { DEFAULT_MODULE_THEME, isDefaultHospitalSku, themeForSku } from './registry'
import { useProductModules } from './useProductModules'

type Props = { children: ReactNode }

const applyCssVars = (primary: string, brand: string, shell: string) => {
  const root = document.documentElement
  root.style.setProperty('--emr-primary', primary)
  root.style.setProperty('--emr-brand', brand)
  root.style.setProperty('--emr-shell', shell)
}

export const ModuleThemeProvider = ({ children }: Props) => {
  const user = useAppSelector(state => state.user.user)
  const isPatient = user?.authable_type?.toLowerCase() === 'patient'
  const { productSku, isLoading: modulesLoading } = useProductModules()

  // Patient portal never follows staff SKU chrome. Staff wait for /product_modules so we do not
  // flash fallback full_hospital_portal → real tenant SKU.
  const chrome = useMemo(() => {
    if (isPatient) return DEFAULT_MODULE_THEME
    if (modulesLoading) return DEFAULT_MODULE_THEME
    return themeForSku(productSku)
  }, [isPatient, modulesLoading, productSku])

  const useDefaultChrome = isPatient || modulesLoading || isDefaultHospitalSku(productSku)

  useEffect(() => {
    applyCssVars(chrome.primary, chrome.shell, chrome.shell)
  }, [chrome])

  const theme = useMemo(() => {
    if (useDefaultChrome) return AppTheme

    return deepMerge(AppTheme, {
      global: {
        colors: {
          brand: chrome.brand,
          primary: chrome.primary,
          greenKelp: chrome.shell,
          accent: chrome.accent,
          control: { border: { color: chrome.primary } }
        }
      },
      button: {
        primary: {
          background: { color: chrome.primary }
        }
      }
    })
  }, [chrome, useDefaultChrome])

  return (
    <Grommet full theme={theme}>
      {children}
    </Grommet>
  )
}
